import type { LocaleCode } from '@ifixit/i18n/types';
import {
   trackPiwikV2AddToCart,
   trackPiwikV2ProductDetailView,
   trackPiwikV2RemoveFromCart,
} from './piwik';
import { trackInPiwik } from './piwik/track-event';
import {
   trackCustomGAEvent,
   trackGA4AddToCart,
   trackGA4RemoveFromCart,
   trackGA4ViewItem,
   trackGA4GrowthbookEvent,
   trackGA4PreferredStore,
   trackGA4PreferredLanguage,
   trackGA4PreferredLocale,
   trackGA4UserPrivilege,
   trackGA4VisitedStore,
   trackGA4PageBreadcrumbs,
   trackGA4FeaturedProducts,
   trackGA4CartUpdate,
} from './google';
import {
   trackPiwikPreferredStore,
   trackPiwikPreferredLanguage,
   trackPiwikPreferredLocale,
   trackPiwikUserPrivilege,
   trackVisitedStore as trackPiwikVisitedStore,
   trackPiwikConsentBanner,
   trackPiwikMobileApp,
   trackPageBreadcrumbEvent,
   trackPiwikFeaturedProducts,
   trackPiwikCartUpdate,
   formatBreadcrumbsForAnalytics,
   type Breadcrumb,
} from './piwik';
import { trackClarityEvent, trackClarityTag } from './clarity';
import { trackTripleWhaleAddToCart } from './triple-whale';

export * from './piwik';
export * from './google';

export type AnalyticsItem = {
   item_id: string | null | undefined;
   item_name: string | null;
   item_variant: string | null;
   numericProductId: string | null;
   quantity: number | null | undefined;
   price: number;
   categories?: string[];
   fulfiller?: string;
   oemPartnership?: string;
};

export type AnalyticsItemsEvent = {
   items: AnalyticsItem[];
   value: number;
   currency: string;
   localeCode?: string;
   shopifyCartId: string | undefined;
};

export type CustomAnalyticsEvent = {
   /**
    * Describes the type of events you want to track.
    * For example, Link Clicks, Videos, Outbound Links, and Form Events.
    */
   eventCategory: string;
   /**
    * The specific action that is taken.
    * For example, with a Video category, you might have a Play, Pause and Complete action.
    */
   eventAction: string;
   /**
    * Usually the title of the element that is being interacted with, to aid with analysis.
    * For example, it could be the name of a Video that was played or the specific
    * form that is being submitted.
    */
   eventName?: string;
   /**
    * A numeric value and is often added dynamically. It could be the cost of a
    * product that is added to a cart, or the completion percentage of a video.
    */
   eventValue?: number;
};

export type GrowthbookEvent = {
   experimentId: string;
   variationId: string;
};

export function trackInAnalyticsViewItem(event: AnalyticsItemsEvent) {
   trackGA4ViewItem(event);
   trackPiwikV2ProductDetailView(event);
   trackClarityEvent('productDetailView');
}

export function trackInAnalyticsAddToCart(event: AnalyticsItemsEvent) {
   trackGA4AddToCart(event);
   trackPiwikV2AddToCart(event);
   trackClarityEvent('addToCart');
   trackTripleWhaleAddToCart(event);
}

export function trackInAnalyticsRemoveFromCart(event: AnalyticsItemsEvent) {
   trackGA4RemoveFromCart(event);
   trackPiwikV2RemoveFromCart(event);
   trackClarityEvent('removeFromCart');
}

export function getCategoriesFromAPICategories(apiCategories: {
   is_tool: boolean;
   item_category: string;
   part_subcategory: string;
}): string[] {
   const firstCategory = apiCategories.is_tool ? 'Tool' : 'Part';
   return [firstCategory, apiCategories.item_category, apiCategories.part_subcategory];
}

export function trackAnalyticsCustomEvent(props: CustomAnalyticsEvent) {
   trackCustomGAEvent(props);
   trackInPiwik(props);
   trackClarityEvent(props.eventCategory);
   const clarityAction = props.eventAction.replace(`${props.eventCategory} - `, '');
   trackClarityTag(props.eventCategory, clarityAction);
}

export function trackAnalyticsGrowthbookEvent({ variationId, experimentId }: GrowthbookEvent) {
   trackGA4GrowthbookEvent({
      eventName: 'experiment_viewed',
      experimentId,
      variationId,
   });
}

// Unified tracking functions for user preferences (Piwik custom dimensions + GA4 events)
export function trackAnalyticsPreferredStore(storeDomain: string) {
   trackPiwikPreferredStore(storeDomain);
   trackGA4PreferredStore(storeDomain);
}

export function trackAnalyticsPreferredLanguage(languageCode: string) {
   trackPiwikPreferredLanguage(languageCode);
   trackGA4PreferredLanguage(languageCode);
}

export function trackAnalyticsPreferredLocale(localeCode: LocaleCode) {
   trackPiwikPreferredLocale(localeCode);
   trackGA4PreferredLocale(localeCode);
}

export function trackAnalyticsUserPrivilege(userPrivilege: string | null) {
   trackPiwikUserPrivilege(userPrivilege);
   trackGA4UserPrivilege(userPrivilege);
}

export function trackAnalyticsVisitedStore() {
   trackPiwikVisitedStore();
   trackGA4VisitedStore();
}

// GA4 consent banner tracking is handled via user_property in google-tag.tsx,
// so we only need to track in Piwik here
export function trackAnalyticsConsentBanner(shownConsentBanner: boolean) {
   trackPiwikConsentBanner(shownConsentBanner);
}

// GA4 mobile app tracking is handled via user_property in google-tag.tsx,
// so we only need to track in Piwik here
export function trackAnalyticsMobileApp(isOnMobileApp: boolean) {
   trackPiwikMobileApp(isOnMobileApp);
}

export function trackAnalyticsPageBreadcrumbs(breadcrumbs: Breadcrumb[]) {
   trackPageBreadcrumbEvent(breadcrumbs);
   const formattedBreadcrumbs = formatBreadcrumbsForAnalytics(breadcrumbs);
   if (formattedBreadcrumbs) {
      trackGA4PageBreadcrumbs(formattedBreadcrumbs);
   }
}

export function trackAnalyticsFeaturedProducts(productSKUs: string[]) {
   trackPiwikFeaturedProducts(productSKUs);
   trackGA4FeaturedProducts(productSKUs);
}

export function trackAnalyticsCartUpdate(event: AnalyticsItemsEvent) {
   trackPiwikCartUpdate(event);
   trackGA4CartUpdate(event);
}
